import {
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';
import { useOptionalEditorStoreApi } from '../store/EditorStoreContext.js';
import {
  formatLength,
  formatNumber,
  parseLength,
  radToDegSafe,
  type DisplayUnit,
} from './format.js';
import { useI18n } from '../../../i18n/i18n.js';

interface CommitInputProps {
  value: string;
  /** Return false to signal rejection; the field then shows the model value again. */
  onCommit: (value: string) => boolean;
  multiline?: boolean;
  inputMode?: 'text' | 'decimal';
  disabled?: boolean;
  /**
   * Arrow up/down: step the value directly (e.g. ±0.5° turns) instead of moving the caret;
   * `big` while Shift is held.
   */
  onStep?: (direction: 1 | -1, big: boolean) => void;
  'data-testid'?: string;
  'aria-label'?: string;
  /** Shown while the field is empty, e.g. the default name. */
  placeholder?: string;
}

/**
 * Edits are committed on blur/Enter as a single undoable command instead of one command
 * per keystroke, which would flood the history. Escape discards the edit.
 */
export function CommitInput({
  value,
  onCommit,
  multiline = false,
  inputMode = 'text',
  disabled = false,
  onStep,
  ...rest
}: CommitInputProps) {
  const [draft, setDraft] = useState<string | null>(null);
  // Blur fires synchronously after Escape, before React re-renders with the reset draft.
  const discard = useRef(false);
  const commit = () => {
    if (!discard.current && draft !== null && draft !== value) onCommit(draft);
    discard.current = false;
    setDraft(null);
  };
  const common = {
    value: draft ?? value,
    onFocus: () => setDraft(value),
    onBlur: commit,
    className: 'input',
    disabled,
    'data-testid': rest['data-testid'],
    'aria-label': rest['aria-label'],
    placeholder: rest.placeholder,
  };
  if (multiline) {
    return <textarea {...common} rows={3} onChange={(e) => setDraft(e.target.value)} />;
  }
  return (
    <input
      {...common}
      inputMode={inputMode}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (onStep && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
          e.preventDefault();
          onStep(e.key === 'ArrowUp' ? 1 : -1, e.shiftKey);
          setDraft(null);
          return;
        }
        if (e.key === 'Enter') e.currentTarget.blur();
        if (e.key === 'Escape') {
          discard.current = true;
          e.currentTarget.blur();
        }
      }}
    />
  );
}

interface LengthFieldProps {
  label: string;
  /** Meters. */
  value: number;
  unit: DisplayUnit;
  onCommit: (meters: number) => boolean;
  /** Minimum allowed value in meters. */
  min?: number;
  testId?: string;
  disabled?: boolean;
}

/** Length input that accepts "4,82", "4820 mm" etc. and always reports meters. */
export function LengthField({
  label,
  value,
  unit,
  onCommit,
  min,
  testId,
  disabled = false,
}: LengthFieldProps) {
  // While an arrow button is held the value runs on here and is saved once on release.
  const [pending, setPending] = useState<number | null>(null);
  const pendingRef = useRef<number | null>(null);
  const stepped = (from: number, direction: 1 | -1, big: boolean) => {
    const next = Math.round((from + direction * (big ? BIG_STEP : LENGTH_STEP)) * 1e4) / 1e4;
    return min !== undefined ? Math.max(min, next) : next;
  };
  return (
    <Field label={label}>
      <span className="stepper-field">
        <CommitInput
          value={formatLength(pending ?? value, unit)}
          inputMode="decimal"
          aria-label={label}
          disabled={disabled}
          {...(testId ? { 'data-testid': testId } : {})}
          onStep={(direction, big) => onCommit(stepped(value, direction, big))}
          onCommit={(text) => {
            const m = parseLength(text, unit);
            if (m === null || (min !== undefined && m < min)) return false;
            return onCommit(m);
          }}
        />
        <Stepper
          label={label}
          disabled={disabled}
          testId={testId}
          onStep={(direction, big) => {
            const next = stepped(pendingRef.current ?? value, direction, big);
            pendingRef.current = next;
            setPending(next);
          }}
          onRelease={() => {
            const next = pendingRef.current;
            pendingRef.current = null;
            setPending(null);
            if (next !== null && Math.abs(next - value) > 1e-9) onCommit(next);
          }}
        />
      </span>
    </Field>
  );
}

/** One click on a field arrow (metres); with Shift ten times as much. */
export const LENGTH_STEP = 0.05;
const BIG_STEP = 0.5;
/** Holding an arrow: first repeat after this long, then every `STEP_REPEAT_MS`. */
const STEP_DELAY_MS = 400;
const STEP_REPEAT_MS = 70;

/**
 * Up/down arrows inside a field: a click steps once, holding keeps stepping faster;
 * `onRelease` ends a run (e.g. to save it as one undo step).
 */
export function Stepper({
  label,
  onStep,
  onRelease,
  disabled = false,
  testId,
}: {
  label: string;
  onStep: (direction: 1 | -1, big: boolean) => void;
  onRelease?: () => void;
  disabled?: boolean;
  testId?: string | undefined;
}) {
  const { m, fmt } = useI18n();
  const timers = useRef<{ wait?: number; repeat?: number }>({});
  const running = useRef(false);
  useEffect(
    () => () => {
      window.clearTimeout(timers.current.wait);
      window.clearInterval(timers.current.repeat);
    },
    [],
  );
  const stop = () => {
    window.clearTimeout(timers.current.wait);
    window.clearInterval(timers.current.repeat);
    timers.current = {};
    if (!running.current) return;
    running.current = false;
    onRelease?.();
  };
  const start = (direction: 1 | -1, big: boolean) => {
    stop();
    running.current = true;
    onStep(direction, big);
    timers.current.wait = window.setTimeout(() => {
      timers.current.repeat = window.setInterval(() => onStep(direction, big), STEP_REPEAT_MS);
    }, STEP_DELAY_MS);
  };
  const button = (direction: 1 | -1) => (
    <button
      type="button"
      tabIndex={-1}
      className="stepper-btn"
      disabled={disabled}
      // The field's name only in the tooltip: labels must not make the button match the field.
      aria-label={direction > 0 ? m.editor.fields.stepUpShort : m.editor.fields.stepDownShort}
      title={fmt(direction > 0 ? m.editor.fields.stepUp : m.editor.fields.stepDown, { label })}
      data-testid={testId ? `${testId}-${direction > 0 ? 'up' : 'down'}` : undefined}
      // Keeps the focus (and a running edit) in the field.
      onPointerDown={(e) => {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        start(direction, e.shiftKey);
      }}
      onPointerUp={stop}
      onPointerCancel={stop}
      onLostPointerCapture={stop}
    >
      {direction > 0 ? '▲' : '▼'}
    </button>
  );
  return (
    <span className="stepper">
      {button(1)}
      {button(-1)}
    </span>
  );
}

interface AngleFieldProps {
  label: string;
  /** Radians. */
  value: number;
  onCommit: (radians: number) => boolean;
  disabled?: boolean;
  testId?: string;
}

export function AngleField({ label, value, onCommit, disabled = false, testId }: AngleFieldProps) {
  return (
    <Field label={label}>
      <CommitInput
        value={`${radToDegSafe(value)} °`}
        inputMode="decimal"
        aria-label={label}
        disabled={disabled}
        {...(testId ? { 'data-testid': testId } : {})}
        onCommit={(text) => {
          const deg = Number(text.replace('°', '').replace(',', '.').trim());
          return Number.isFinite(deg) && onCommit((deg * Math.PI) / 180);
        }}
      />
    </Field>
  );
}

const QUARTER_TURN = Math.PI / 2;
const FINE_TURN = Math.PI / 360;
/** Held ±0.5° button: first repeat after the delay, then every interval; 2° steps later on. */
const HOLD_DELAY_MS = 400;
const HOLD_REPEAT_MS = 100;
const HOLD_FAST_AFTER_MS = 2000;
const FAST_TURN = Math.PI / 90;

/**
 * Repeats a turn while a button is held, so larger angles need no endless clicking. The whole
 * hold becomes one undo step.
 */
function useHeldTurn(onRotateBy: (delta: number) => boolean) {
  const store = useOptionalEditorStoreApi();
  const turn = useRef(onRotateBy);
  useEffect(() => {
    turn.current = onRotateBy;
  }, [onRotateBy]);
  const timer = useRef<number | null>(null);
  const finish = useRef<(() => void) | null>(null);
  const stop = () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = null;
    finish.current?.();
    finish.current = null;
  };
  useEffect(() => stop, []);
  const start = (dir: 1 | -1) => {
    stop();
    finish.current = store?.getState().batch() ?? null;
    const began = performance.now();
    turn.current(dir * FINE_TURN);
    const tick = () => {
      const fast = performance.now() - began > HOLD_FAST_AFTER_MS;
      if (!turn.current(dir * (fast ? FAST_TURN : FINE_TURN))) {
        stop();
        return;
      }
      timer.current = window.setTimeout(tick, HOLD_REPEAT_MS);
    };
    timer.current = window.setTimeout(tick, HOLD_DELAY_MS);
  };
  return { start, stop };
}

interface RotationControlProps {
  label?: string;
  /** Current angle (radians). Without it the field takes a relative turn ("Drehen um"). */
  value?: number;
  /** Turns by `delta` radians (counter-clockwise positive); false if rejected. */
  onRotateBy: (delta: number) => boolean;
  /** Back onto a right angle to the walls around it (objects). */
  onStraighten?: () => void;
  /** False while it already stands straight. */
  canStraighten?: boolean;
  disabled?: boolean;
  testId?: string;
}

/**
 * The same turning controls everywhere: quarter turns, ±0.5° steps (also arrow up/down in the
 * field) and an exact angle. Positive angles turn counter-clockwise, like the field value.
 */
export function RotationControl({
  label,
  value,
  onRotateBy,
  onStraighten,
  canStraighten = true,
  disabled = false,
  testId,
}: RotationControlProps) {
  const { m, fmt } = useI18n();
  const t = m.editor.rotation;
  const text = label ?? (value === undefined ? t.by : t.angle);
  const step = (dir: 1 | -1) => onRotateBy(dir * FINE_TURN);
  const fine = formatNumber(0.5, 1);
  const held = useHeldTurn(onRotateBy);
  const buttons: { label: string; title: string; delta: number; id: string; hold?: 1 | -1 }[] = [
    { id: 'ccw90', label: '⟲ 90°', title: t.ccw90, delta: QUARTER_TURN },
    { id: 'minus', label: `−${fine}°`, title: t.cwFine, delta: -FINE_TURN, hold: -1 },
    { id: 'plus', label: `+${fine}°`, title: t.ccwFine, delta: FINE_TURN, hold: 1 },
    { id: 'cw90', label: '⟳ 90°', title: t.cw90, delta: -QUARTER_TURN },
  ];
  return (
    <div className="rotation-control" data-testid={testId}>
      <Field label={text}>
        <CommitInput
          value={`${radToDegSafe(value ?? 0)} °`}
          inputMode="decimal"
          aria-label={text}
          disabled={disabled}
          onStep={step}
          {...(testId ? { 'data-testid': `${testId}-input` } : {})}
          onCommit={(t) => {
            const deg = Number(t.replace('°', '').replace(',', '.').trim());
            if (!Number.isFinite(deg)) return false;
            const rad = (deg * Math.PI) / 180;
            const delta = value === undefined ? rad : rad - value;
            return delta === 0 || onRotateBy(delta);
          }}
        />
      </Field>
      <div className="rotation-buttons" role="group" aria-label={fmt(t.steps, { label: text })}>
        {buttons.map((b) => (
          <button
            key={b.id}
            type="button"
            className="btn btn-compact"
            title={b.title}
            disabled={disabled}
            {...(b.hold
              ? {
                  // Pointer presses turn (and repeat while held); a click without a pointer
                  // (Enter/Space, detail 0) turns once.
                  onPointerDown: (e: PointerEvent<HTMLButtonElement>) => {
                    if (e.button !== 0) return;
                    e.currentTarget.setPointerCapture(e.pointerId);
                    held.start(b.hold as 1 | -1);
                  },
                  onPointerUp: held.stop,
                  onPointerCancel: held.stop,
                  onLostPointerCapture: held.stop,
                  onContextMenu: (e: MouseEvent) => e.preventDefault(),
                  onClick: (e: MouseEvent) => {
                    if (e.detail === 0) onRotateBy(b.delta);
                  },
                }
              : { onClick: () => onRotateBy(b.delta) })}
            {...(testId ? { 'data-testid': `${testId}-${b.id}` } : {})}
          >
            {b.label}
          </button>
        ))}
      </div>
      {onStraighten && (
        <button
          type="button"
          className="btn btn-ghost"
          disabled={disabled || !canStraighten}
          onClick={onStraighten}
          {...(testId ? { 'data-testid': `${testId}-straighten` } : {})}
        >
          ⊥ {t.straighten}
        </button>
      )}
    </div>
  );
}

/** Locks an element on the canvas: no dragging, turning or resizing; panel values stay editable. */
export function LockToggle({
  locked,
  onChange,
  testId,
}: {
  locked: boolean;
  onChange: (locked: boolean) => void;
  testId?: string;
}) {
  const t = useI18n().m.editor.lock;
  return (
    <>
      <label className="checkbox">
        <input
          type="checkbox"
          checked={locked}
          onChange={(e) => onChange(e.target.checked)}
          {...(testId ? { 'data-testid': testId } : {})}
        />
        {t.label}
      </label>
      {locked && <p className="panel-hint">{t.hint}</p>}
    </>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
    </label>
  );
}

export function ReadOnly({
  label,
  value,
  testId,
}: {
  label: string;
  value: string;
  testId?: string;
}) {
  return (
    <div className="readonly-row">
      <span className="muted">{label}</span>
      <strong data-testid={testId}>{value}</strong>
    </div>
  );
}
